import { createSelector } from "reselect";
// eslint-disable-next-line @logicsoftware/ep-custom/no-cross-feature-imports
import { getCurrentUserId, getIsGuest } from "~/app/selectors/common";
import { ApprovalMessageStatus, ApproverStatus } from "~/const/approval";
import { dateUtility } from "~/EasyProjectsCommon/Dates/DateUtility";
import { Entities } from "~/normalizr/entities";
import {
    ApprovalsEntities,
    ApprovalsModel,
    emptyMessageInfoToScrollTo,
    FilesEntities,
    Message,
    MessageInfoToScrollTo,
    MessageOption,
    MessageOrWorkflowEvent,
    Messages,
    ProjectOrTask,
    RootMessageBoardState,
    WorkflowEvent,
} from "../types";
import { getKey } from "../utils/getKey";
import { getCurrentUserApprover } from "./approvals";

export const getMessagesLoaded = (state: RootMessageBoardState, ownerInfo: ProjectOrTask): boolean =>
    state?.messageBoard?.messages?.[getKey(ownerInfo)]?.loaded;

export const getMessagesData = (state: RootMessageBoardState, ownerInfo: ProjectOrTask): Messages =>
    state?.messageBoard?.messages[getKey(ownerInfo)];

export const getMessagesReactions = (
    state: RootMessageBoardState,
    ownerInfo: ProjectOrTask
): Entities<Entities<number[]>> => state?.messageBoard?.messages[getKey(ownerInfo)]?.reactions;

export const getReactionsByMessageId = createSelector(
    getMessagesReactions,
    (_, ownerInfo: ProjectOrTask) => ownerInfo,
    (_, __, id: number) => id,
    getCurrentUserId,
    (allReactions, _, id, userId) => {
        const reactions = allReactions?.[id] || {};
        const activeReactions: Dictionary<boolean> = {};

        for (const r of Object.keys(reactions)) {
            activeReactions[r] = reactions[r].includes(userId);
        }

        return { activeReactions, reactions };
    }
);

export const getReactionsByMessageIdFactory = (messageId: number) =>
    createSelector(getMessagesReactions, getCurrentUserId, getIsGuest, (allReactions, userId, isGuest) => {
        const reactions = allReactions?.[messageId] || {};

        return Object.keys(reactions)
            .map(key => {
                const users = reactions[key];
                return {
                    emoji: key,
                    users: !isGuest ? users : undefined,
                    counter: users.length,
                    isActive: users.includes(userId),
                };
            })
            .filter(x => x.counter > 0);
    });

export const getMessagesEntities = (state: RootMessageBoardState, ownerInfo: ProjectOrTask): Entities<Message> =>
    getMessagesData(state, ownerInfo)?.messages || {};

export const getMessage = (state: RootMessageBoardState, ownerInfo: ProjectOrTask, id: number): Message =>
    getMessagesEntities(state, ownerInfo)?.[id];

export const getEvents = (state: RootMessageBoardState, ownerInfo: ProjectOrTask) =>
    getMessagesData(state, ownerInfo).events;
export const getApprovals = (state: RootMessageBoardState, ownerInfo: ProjectOrTask) =>
    getMessagesData(state, ownerInfo).approvals;
export const getFiles = (state: RootMessageBoardState, ownerInfo: ProjectOrTask): FilesEntities =>
    getMessagesData(state, ownerInfo).files || {};
export const getIsApprovalsEnabled = (state: RootMessageBoardState, ownerInfo: ProjectOrTask) =>
    getMessagesData(state, ownerInfo).isApprovalsEnabled;

const emptySelfAddedMessageIds: number[] = [];
export const getSelfAddedMessageIds = (state: RootMessageBoardState): number[] =>
    state?.messageBoard?.selfAddedMessageIds || emptySelfAddedMessageIds;
export const getEditMessageId = createSelector(getMessagesData, (message): [number, boolean] => [
    message.editMessageId,
    message.reply,
]);

export const getMessages = createSelector(getMessagesEntities, entities => Object.values(entities));

export const getMessageOptionModel = (state: RootMessageBoardState, ownerInfo: ProjectOrTask) =>
    getMessagesData(state, ownerInfo).MessageOptionModel;
export const getMessageInfoToScrollTo = (state: RootMessageBoardState): MessageInfoToScrollTo =>
    state?.messageBoard?.messageInfoToScrollTo || emptyMessageInfoToScrollTo;
export const getIsLastMessageVisible = (state: RootMessageBoardState): boolean =>
    state?.messageBoard?.isLastMessageVisible;
export const getIsFirstNewMessageVisible = (state: RootMessageBoardState): boolean =>
    state?.messageBoard?.isFirstNewMessageVisible;

export const getExpendedAttached = (state: RootMessageBoardState) => state?.messageBoard?.expandedAttached;

const getApprovalStatus = (approval: ApprovalsModel) => {
    const approvers = Object.values(approval.approvers);

    if (approval.IsActive) {
        const everyApproved = approvers.every(x => x.Status === ApproverStatus.Approved);

        if (everyApproved) {
            return ApprovalMessageStatus.approved;
        }

        const everyRejected = approvers.every(x => x.Status === ApproverStatus.Rejected);

        if (everyRejected) {
            return ApprovalMessageStatus.rejected;
        }

        return ApprovalMessageStatus.approval;
    }

    const result = approvers.find(x => x.Status === ApproverStatus.Approved || x.Status === ApproverStatus.Rejected);

    if (result) {
        return ApprovalMessageStatus.outdated;
    }
};

type MessagesSortByDate = (
    messages: Message[],
    events: WorkflowEvent[],
    approvals: ApprovalsEntities,
    mbFilter: MessageOption,
    selfAddedIds: number[]
) => MessageOrWorkflowEvent[][];

const getMessagesSortByDate: MessagesSortByDate = (messages, events, approvals, messageOptionModel, selfAddedIds) => {
    const allMessages = [...messages, ...events];
    let list: MessageOrWorkflowEvent[] = [];

    if (messageOptionModel.MessagesVisible) {
        list = [...messages];
    } else {
        list = messages.filter(
            m => (messageOptionModel.FilesVisible && m.Files?.length) || selfAddedIds.includes(m?.MessageId)
        );
    }

    if (messageOptionModel.EventsVisible) {
        list.push(...events);
    }

    return [
        list
            .sort((a, b) => {
                return a.Date.getTime() - b.Date.getTime();
            })
            .map(x => {
                let formatEditedAt = "";

                if ("EditedAt" in x) {
                    formatEditedAt = dateUtility.getElapsedTimeRepresentation(x.EditedAt, {
                        includeTime: true,
                        isUtc: true,
                    });
                }

                let approvalStatus;

                if ("Approval" in x) {
                    const approval = approvals[x.Approval];
                    approvalStatus = getApprovalStatus(approval);
                }

                return {
                    ...x,
                    formatEditedAt,
                    approvalStatus,
                };
            }),
        allMessages,
    ];
};

export const getMessagesList = createSelector(
    getMessages,
    getEvents,
    getApprovals,
    getMessageOptionModel,
    getSelfAddedMessageIds,
    getMessagesSortByDate
);

export const getNewApprovalsByIds = createSelector(
    state => state.common.currentUserId,
    getApprovals,
    (state, ownerInfo, ids: number[]) => ids,
    getMessages,
    (currentUserId, approvals, approvalIds, messages) => {
        const creators = messages
            .filter(m => !!m.Approval)
            .reduce<Entities<number>>((acc, message) => ((acc[message.Approval] = message.UserId), acc), {});
        const items = approvalIds.map(id => approvals[id]);
        return items.filter(
            a => creators[a.Id] === currentUserId || getCurrentUserApprover(a.approvers, currentUserId)?.IsNew
        );
    }
);

const isNew = (x: Message | WorkflowEvent) => x.IsNew;
const emptyMessageIds: number[] = [];

export const getNewMessageIds = createSelector(getMessages, messages => messages.filter(isNew).map(x => x.MessageId));
export const getNewVisibleMessageIds = createSelector(getNewMessageIds, getMessageOptionModel, (messages, options) =>
    options?.MessagesVisible ? messages : emptyMessageIds
);

export const getNewFilesCount = createSelector(
    getMessages,
    messages => messages.filter(isNew).filter(m => !!m.Files?.length).length
);
export const getNewEventsCount = createSelector(getEvents, events => events.filter(isNew).length);

export const getCurrentUserActiveApprovalId = createSelector(
    getMessages,
    getApprovals,
    getMessageOptionModel,
    getCurrentUserId,
    (messages, approvals, options, currentUserId) =>
        (options?.MessagesVisible &&
            messages
                .filter(m => m.Approval)
                .find(
                    a =>
                        approvals[a.Approval].IsActive &&
                        getCurrentUserApprover(approvals[a.Approval].approvers, currentUserId)?.Status ===
                            ApproverStatus.None
                )?.MessageId) ||
        null
);
